import React from 'react'
import data from './data.json'
import { Intro, Layout, Header, SectionTitle } from '../../../../components'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      />
      <Layout
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      >
        <Intro>
          <p>
            Action language can appear within an experience in the form of CTAs
            (Calls to Action), user prompts and feedback messages, as described
            below.
          </p>
        </Intro>
        <SectionTitle>CTA</SectionTitle>
        <p>
          Calls to action (CTAs) are most often used in buttons, but can also be
          found in titles and links, depending on the interaction. Note that CTA
          usage always adheres to the M{'&'}T Bank brand tenets for voice and
          tone, specifically that we are always approachable, clear, helpful and
          conversational with users.
        </p>
        <h3 class="c-title-3--light">CTA Short</h3>
        <div class="l-flex-row">
          <div class="l-flex-col--xs-8">
            <p>
              A grouping of three or four words, CTA Short is usually found in
              headlines, subheads, section header titles and links. These are
              best when asking the user to complete a single action.
            </p>
          </div>
          <div class="l-flex-col--xs-4">
            <ul class="c-unordered-list">
              <li class="c-unordered-list__item">Choose a debit card</li>
              <li class="c-unordered-list__item">Create your PIN</li>
              <li class="c-unordered-list__item">Make a deposit</li>
            </ul>
          </div>
        </div>
        <h3 class="c-title-3--light">CTA Long</h3>
        <div class="l-flex-row">
          <div class="l-flex-col--xs-8">
            <p>
              CTA Long is usually a short sentence, and functions similarly to
              CTA Short, so can be found in similar locations. These are best
              when asking the user to complete a series of actions in service of
              a single goal.
            </p>
          </div>
          <div class="l-flex-col--xs-4">
            <ul class="c-unordered-list">
              <li class="c-unordered-list__item">
                Now, let’s continue with the second applicant
              </li>
              <li class="c-unordered-list__item">
                Let’s answer some account activity questions.
              </li>
              <li class="c-unordered-list__item">
                Set up your Online Banking profile.
              </li>
            </ul>
          </div>
        </div>
        <h3 class="c-title-3--light">Button CTA</h3>
        <div class="l-flex-row">
          <div class="l-flex-col--xs-8">
            <p>
              These are short, ideally single-word touts, with a maximum of four
              words. A button CTA should act as a bridge to the next page so
              that users know exactly where they are headed.
            </p>
          </div>
          <div class="l-flex-col--xs-4">
            <ul class="c-unordered-list l-margin-top__xlarge">
              <li class="c-unordered-list__item">Continue</li>
              <li class="c-unordered-list__item">Accept</li>
              <li class="c-unordered-list__item">Apply Now</li>
              <li class="c-unordered-list__item">Open Account</li>
            </ul>
          </div>
        </div>
        <hr />
        <SectionTitle>User Prompts</SectionTitle>
        <p>
          Typically found in subheads and form entry titles, these prompts
          direct the user to input, download, or confirm information. They can
          take the form of commands or questions, and the user will respond by
          interacting with the associated button, link or form input components.
          When writing questions, adhere to M{'&'}T Bank brand tenets and focus
          the “asks” on what’s important in order to move the user efficiently
          through the flow. Confirmation questions tend to be binary, asked when
          there is a specific need for a “yes” or “no” answer.
        </p>
        <ul class="c_unordered_list">
          <li class="c-unordered-list__item">
            For your security, please verify your identity by answering the
            following questions.
          </li>
          <li class="c-unordered-list__item">
            Please open, read and save the agreements below prior to acceptance.
          </li>
          <li class="c-unordered-list__item">Select those that apply:</li>
          <li class="c-unordered-list__item">
            Would you like to order checks now?
          </li>
          <li class="c-unordered-list__item l-margin-bottom--xxlarge">
            How many times per month will you withdraw or deposit cash?
          </li>
        </ul>

        <p>
          When appearing in standalone checkbox components, user prompts are
          written in the first person from the user's perspective. Write in the
          active voice with positive verbs so the user can be sure what they are
          agreeing to.
        </p>
        <div class="c-checkbox__container">
          <div>
            <input class="c-checkbox__input" type="checkbox" id="checkbox3" />
            <label class="c-checkbox u-color--white" htmlFor="checkbox3">
              By checking this box, I am electing to receive my statement and
              notices online.
              <span class="c-checkbox__checkmark">
                <svg
                  class="c-checkbox__checkmark-icon"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 32 32"
                  fill="#fff"
                >
                  <path d="M27 4l-15 15-7-7-5 5 12 12 20-20z"></path>
                </svg>
              </span>
            </label>
          </div>
        </div>

        <hr />
        <SectionTitle>Feedback and Error Messages</SectionTitle>
        <p>
          Warning and error messages will direct the user to take action to
          prevent an unwanted outcome. This will be different from the primary
          CTA on the screen, so use the appropriate presentation of the Feedback
          and Error Message to be specific about what needs to be done. If the
          action to be taken is located on a different page, include a link to
          that page.
        </p>
        <div class="c-notification--inline-error">
          <div class="c-notification__icon">
            <i class="icon icon--error"></i>
          </div>
          <div class="c-notification__content">
            Your account has been opened, but your card transaction has been
            declined. Please deposit at least $25.00 in a branch or online
            within 60 days to keep your account open.
          </div>
        </div>

        <div class="c-notification--inline-warning l-margin-top--large">
          <div class="c-notification__icon">
            <i class="icon icon--error"></i>
          </div>
          <div class="c-notification__content">
            You'll need to visit a branch to open a joint account.{' '}
            <a
              className="link"
              href="https://locations.mtb.com/?ch=psg&cid=psg|2020brand|443567945210|google|28238075849|aud-425830411114:kwd-172559262&matchtype=p&adid=443567945210&addisttype=g&kxconfid=ug485menz&campaign=423329009&campaignid=psg&siteid=google&gclid=Cj0KCQjwjer4BRCZARIsABK4QeU_Y3nt8LJInQC6l1waSrjiXAazpPILgwtzMfwCiHYP5Xu0NywgRDAaAqFGEALw_wcB"
            >
              Find an M{'&'}T branch near you.
            </a>
          </div>
        </div>
      </Layout>
    </>
  )
}
